import { DriveStep } from 'driver.js';
import { TourAlignment, TourSide, TourStepAction } from '../enums/tour-config';
import { PermissionCode } from '../enums/permission-code';
export class TourStep {
  driverConfig!: DriveStep;
  action?: TourStepAction;
  actionTargetId?: string;
  requireDynamicDOM?: boolean = false;
  permissionMatchType?: 'ANY' | 'ALL' = 'ANY';
  permissions?: string[] = [];
  descriptionWithPermission?: string;
  titleWithPermission?: string;
}

export interface VisibleEntry {
  kind: 'visible';
  step: TourStep;
}
export interface GhostEntry {
  kind: 'ghost';
  step: TourStep;
}

export type Entry = VisibleEntry | GhostEntry;

export interface TourMatcher {
  match: (params: Record<string, string>) => boolean;
  steps: TourStep[];
}

export interface TourRouteConfig {
  defaultSteps: TourStep[];
  matchers?: TourMatcher[];
  optionalSteps?: TourStep[];
}

const headerSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-search',
      popover: {
        title: 'Search',
        description: 'Search here to navigate across pages and subpages.',
        side: TourSide.Bottom,
        align: TourAlignment.Start,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-invite-organization',
      popover: {
        title: 'Invite Organizations to Join',
        description:
          'Share the registration link with an organization’s administrator to invite them to join the ACC Data Hub Portal.',
        side: TourSide.Bottom,
        align: TourAlignment.End,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-notification',
      popover: {
        title: 'Notifications',
        description: 'View all your notifications here.',
        side: TourSide.Bottom,
        align: TourAlignment.End,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-profile',
      popover: {
        title: 'My Profile',
        description:
          'Access your profile details and helpful options from here.',
        side: TourSide.Bottom,
        align: TourAlignment.End,
      },
    },
  },
];

const sideMenuSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-menu-item-dashboard',
      popover: {
        title: 'Dashboard',
        description:
          'Your home view where you will get a summary of everything',
        side: TourSide.Right,
        align: TourAlignment.Center,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-menu-item-sub-leads',
  },
  {
    driverConfig: {
      element: '#tour-menu-item-leads',
      popover: {
        title: 'Leads',
        description:
          'Discover and manage businesses from multiple sources. Track status, add notes, and more.',
        side: TourSide.Right,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-menu-item-files',
      popover: {
        title: 'Files',
        description:
          'Access documents uploaded by you and your team. Keep records and shared files organized.',
        side: TourSide.Right,
        align: TourAlignment.Center,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-menu-item-sub-deeproots',
  },
  {
    driverConfig: {
      element: '#tour-menu-item-deeproots',
      popover: {
        title: 'Deeproots',
        description: 'Manage your lead criteria and create invite links.',
        side: TourSide.Right,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-menu-item-cdfis',
      popover: {
        title: 'Organizations',
        description:
          'Review Organization profiles and prepare your business record for lending conversations.',
        side: TourSide.Right,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-menu-item-achievements',
      popover: {
        title: 'Achievements',
        description:
          'Track your milestones, view unlocked badges, and celebrate your organization’s progress across the platform.',
        side: TourSide.Right,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-menu-item-business',
      popover: {
        title: 'Business Needs',
        description:
          'Keep your business profile current so Organizations can quickly understand your location, funding needs, and support priorities.',
        side: TourSide.Right,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-menu-item-flags',
      popover: {
        title: 'My Flags',
        description:
          'Track and review the status of issues, data corrections, and feedback you have submitted.',
        side: TourSide.Right,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-upload-logo',
      popover: {
        title: 'Upload Logo',
        description: 'View your organization profile here.',
        side: TourSide.Right,
        align: TourAlignment.End,
      },
    },
    permissions: [
      PermissionCode.MANAGE_CDFI_PROFILE,
      PermissionCode.ADMIN_PRIVILEGES,
    ],
    descriptionWithPermission: 'Manage your organization logo here.',
  },
  {
    driverConfig: {
      element: '#tour-side-menu-toggle',
      popover: {
        title: 'Menu Toggle',
        description:
          'Use this button to expand or collapse the side navigation menu for easier access and more workspace.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
  },
];

const dashboardSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-pending-actions-section',
      popover: {
        title: 'Pending Actions Overview',
        description: 'View pending actions that need your attention.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-toggle-status-cards',
  },
  {
    driverConfig: {
      element: '#tour-dashboard-leads-status',
      popover: {
        title: 'Leads Dashboard',
        description:
          'View your lead status, matches, inquiries, criteria, and links in one place.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-toggle-status-cards',
  },
  {
    driverConfig: {
      element: '#tour-arcgis-tabs',
      popover: {
        title: 'Dashboard Tabs',
        description: 'Switch between maps using these tabs.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-map-tools-insights-0',
      popover: {
        title: 'Map Tools & Insights',
        description: 'Use these tools to filter map data.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-map-tools-insights-1',
      popover: {
        title: 'Site Intelligence',
        description:
          'Analyze locations using geospatial intelligence and mapping tools.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-map-tools-insights-2',
      popover: {
        title: 'Bookmarks',
        description:
          'Add and access site bookmarks and locations quickly from here.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-gbra-map',
      popover: {
        title: 'GBRA Eligibility',
        description:
          'Check eligibility areas and related geographic insights here.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
];

const smeDashboardSteps = [
  {
    driverConfig: {
      element: '#tour-organizations-assigned',
      popover: {
        title: 'Organizations assigned',
        description:
          'View the number of organizations currently assigned to your business profile.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-organizations-funded',
      popover: {
        title: 'Organizations funded',
        description:
          'See how many organizations have provided funding to your business.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-total-funded',
      popover: {
        title: 'Total funded',
        description:
          'Total amount of funding that has been disbursed to your business.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-profile-views',
      popover: {
        title: 'Profile Views',
        description:
          'Total number of times your bio has been viewed by Organizations and its users.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-business-views',
      popover: {
        title: 'Business views',
        description:
          'Total number of times your business profile has been viewed by Organizations and its users.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-recent-activity',
      popover: {
        title: 'Recent Activity',
        description:
          'Stay updated with the latest activities or milestones here.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
];

const leadsSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-lead-filter',
      popover: {
        title: 'Filter Leads',
        description:
          'Filter leads based on status to quickly find what you’re looking for.',
        side: TourSide.Right,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-lead-search',
      popover: {
        title: 'Search Leads',
        description: 'Search leads by name, address, or other criteria.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-lead-item-0',
      popover: {
        title: 'Assigned Leads',
        description:
          'These are businesses assigned to your CDFI. Each item represents a lead you can actively work on.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-lead-item-source-0',
      popover: {
        title: 'Lead Source',
        description:
          'Source of this lead to understand where it originated from.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-lead-item-status-0',
      popover: {
        title: 'Lead Status',
        description: 'Status of this lead to understand its current progress.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-lead-item-notes-0',
      popover: {
        title: 'Lead Notes',
        description:
          'Add notes to track conversations, follow-ups, and important updates for this lead.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-lead-item-unassign-0',
      popover: {
        title: 'Unassign Lead',
        description:
          'Use this option to remove this business from your assigned leads.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
];

const viewRecordDetails: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-toggle-assign-btn',
      popover: {
        title: 'Assigned To',
        description:
          'Displays whether this business is assigned to your CDFI or not.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-view-leads-history',
      popover: {
        title: 'Leads History',
        description:
          'View the complete history of leads and interactions related to this business.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    action: TourStepAction.Checkbox,
    actionTargetId: 'tour-record-item-checkbox',
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-view-opportunity-indicator',
      popover: {
        title: 'View Opportunity Indicator',
        description:
          'Use this indicator to track opportunity performance and monitor its overall progress.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
    action: TourStepAction.Click,
    actionTargetId: 'tour-view-opportunity-indicator',
  },
  {
    driverConfig: {
      element: '#tour-score-data-source',
      popover: {
        title: 'Select data source for opportunity indicator',
        description:
          'Choose the data source you want to use for calculating the Opportunity Indicator:',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
    action: TourStepAction.Click,
    actionTargetId: 'tour-display-outreach-data',
  },
  {
    driverConfig: {
      element: '#tour-oppurtunity-indicator',
      popover: {
        title: 'Opportunity Score Details',
        description:
          'Review how the opportunity score is calculated using Business Background, Debt Service & Financial Leverage, and Requested Credit Facility data. This section also provides insights into credit exposure, CFADS, and working capital requirements to support decision making.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-score-notes',
      popover: {
        title: 'Opportunity Score Notes',
        description: 'Maximum Recommended Total Credit Exposure',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
    action: TourStepAction.Click,
    actionTargetId: 'tour-toggle-business-overview',
  },
  {
    driverConfig: {
      element: '#tour-business-overview-section',
      popover: {
        title: 'Business overview data inputs',
        description: 'Business overview data inputs',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-view-notes',
      popover: {
        title: 'Notes',
        description:
          'Access notes and comments added by team members for this business.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-export-to-pdf',
      popover: {
        title: 'Export to PDF',
        description:
          'Download this business record as a PDF for sharing or offline use.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-visibility-mode',
      popover: {
        title: 'Visibility Mode',
        description:
          'Control who can view this business record by adjusting its visibility settings.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-share-details',
      popover: {
        title: 'Share Business Details',
        description: 'Share this business record with others CDFIs.',
        side: TourSide.Left,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
    action: TourStepAction.Click,
    actionTargetId: 'tour-view-opportunity-indicator',
  },
  {
    driverConfig: {
      element: '#tour-assigned-to',
      popover: {
        title: 'Assigned to',
        description: 'This record has been assigned to the following CDFIs.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-matched-cdfis',
      popover: {
        title: 'Matched CDFIs',
        description:
          'List of matched CDFIs based on the lead criteria set by them. Click on each CDFI to view details of the match.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-location-intelligence',
      popover: {
        title: 'Location Intelligence',
        description:
          'Access location intelligence data for this business, including geographic context and nearby points of interest.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-lead-info',
      popover: {
        title: 'Lead information',
        description: 'Below are the details of lead',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'secondary-popup-close',
    requireDynamicDOM: true,
  },
];

const findLeadsSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-find-leads-controls',
      popover: {
        title: 'Lead source selector',
        description:
          'Select the source you want to discover leads from, including WNCSBI, Data Axle, and more.',
        side: TourSide.Right,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-find-leads-search',
      popover: {
        title: 'Search Leads',
        description: 'Search leads by name within the selected source.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-find-leads-subnav',
      popover: {
        title: 'Filter leads',
        description:
          'Filter leads by various criteria within the selected source.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
];

const customBusinessSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-custom-add-record',
      popover: {
        title: 'Add Business Record',
        description:
          'Create a new business record by entering details manually.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-record-assigned-item',
      popover: {
        title: 'Business Record',
        description: 'Each record represents a business added to the system.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
    action: TourStepAction.Checkbox,
    actionTargetId: 'tour-record-item-checkbox',
  },
  {
    driverConfig: {
      element: '#tour-custom-view-map',
      popover: {
        title: 'View on Map',
        description: 'Open this business location directly on the map.',
        side: TourSide.Bottom,
        align: TourAlignment.End,
      },
    },
    action: TourStepAction.Checkbox,
    actionTargetId: 'tour-record-item-checkbox',
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-custom-record-creator-0',
      popover: {
        title: 'Record Creator',
        description:
          'This tag appears when the business was created by your CDFI.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-record-visibility-0',
      popover: {
        title: 'Visibility',
        description:
          'Controls whether this business record is visible only to your CDFI or shared with others.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-record-assigned-item-assigned-to',
      popover: {
        title: 'Assigned To',
        description: 'Displays which CDFI this business is assigned to.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-record-item-view',
  },
  ...viewRecordDetails,
];

const initialDataAxleSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-data-axle-search',
      popover: {
        title: 'Search Businesses',
        description:
          'Search for businesses using various filters in Data Axle’s external data source.',
        side: TourSide.Bottom,
        align: TourAlignment.Start,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-search-type-dropdown-toggle',
  },
  {
    driverConfig: {
      element: '#tour-dataxle-search-options',
      popover: {
        title: 'Search Type',
        description: 'Select the type of search you want to perform.',
        side: TourSide.Bottom,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
    action: TourStepAction.Click,
    actionTargetId: 'tour-search-type-dropdown-toggle',
  },
];

const wncsbiSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-record-assigned-item',
      popover: {
        title: 'Business Record',
        description: 'Each record represents a business added to the system.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
    action: TourStepAction.Checkbox,
    actionTargetId: 'tour-record-item-checkbox',
  },
  {
    driverConfig: {
      element: '#tour-custom-view-map',
      popover: {
        title: 'View on Map',
        description: 'Open this business location directly on the map.',
        side: TourSide.Bottom,
        align: TourAlignment.End,
      },
    },
    action: TourStepAction.Checkbox,
    actionTargetId: 'tour-record-item-checkbox',
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-record-assigned-item-assigned-to',
      popover: {
        title: 'Assigned To',
        description: 'Displays which CDFI this business is assigned to.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-record-item-view',
  },
  ...viewRecordDetails,
];

const inquiriesSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-record-assigned-item',
      popover: {
        title: 'Business Record',
        description: 'Each record represents a business added to the system.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
    action: TourStepAction.Checkbox,
    actionTargetId: 'tour-record-item-checkbox',
  },
  {
    driverConfig: {
      element: '#tour-custom-view-map',
      popover: {
        title: 'View on Map',
        description: 'Open this business location directly on the map.',
        side: TourSide.Bottom,
        align: TourAlignment.End,
      },
    },
    action: TourStepAction.Checkbox,
    actionTargetId: 'tour-record-item-checkbox',
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-record-assigned-item-assigned-to',
      popover: {
        title: 'Assigned To',
        description: 'Displays which CDFI this business is assigned to.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-record-item-view',
  },
  ...viewRecordDetails,
];

const matchedRecordsSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-record-assigned-item',
      popover: {
        title: 'Business Record',
        description: 'Each record represents a business added to the system.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
    action: TourStepAction.Checkbox,
    actionTargetId: 'tour-record-item-checkbox',
  },
  {
    driverConfig: {
      element: '#tour-record-assigned-item-assigned-to',
      popover: {
        title: 'Assigned To',
        description: 'Displays which CDFI this business is assigned to.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-record-item-view',
  },
  ...viewRecordDetails,
];

const filesSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-files-filter',
      popover: {
        title: 'Filter Files',
        description:
          'Apply filters to organize and locate files more efficiently.',
        side: TourSide.Bottom,
        align: TourAlignment.End,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-files-search',
      popover: {
        title: 'Search Files',
        description: 'Search for specific files by name or other criteria.',
        side: TourSide.Bottom,
        align: TourAlignment.End,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-my-files-tab',
      popover: {
        title: 'My Files',
        description:
          'View files uploaded by you for quick access and management.',
        side: TourSide.Bottom,
        align: TourAlignment.Start,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-shared-files-tab',
      popover: {
        title: 'Shared Files',
        description: 'Browse files shared across your organization.',
        side: TourSide.Bottom,
        align: TourAlignment.Start,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-files-upload',
      popover: {
        title: 'Upload Files',
        description: 'Upload documents and files to store and share.',
        side: TourSide.Left,
        align: TourAlignment.End,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-file-item-info',
      popover: {
        title: 'View operations',
        description: 'Click here to view operations',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-file-item-share',
      popover: {
        title: 'Share file',
        description: 'Click here to share files',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-file-item-download',
      popover: {
        title: 'Download file',
        description: 'Click here to download files',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-file-item-delete',
      popover: {
        title: 'Delete file',
        description: 'Click here to delete files',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
  },
];

const linksSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-filter',
      popover: {
        title: 'Filter Links',
        description:
          'Use filters to quickly find links by all, active, or inactive.',
        side: TourSide.Bottom,
        align: TourAlignment.End,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-link-search',
      popover: {
        title: 'Search Links',
        description:
          'Search for specific outreach links by name or other criteria to find them quickly.',
        side: TourSide.Bottom,
        align: TourAlignment.End,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-links-generate',
      popover: {
        title: 'Generate Link',
        description: 'Create a new outreach link to share records.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-outreach-item-0',
      popover: {
        title: 'Outreach Links',
        description:
          'View and manage all outreach links created for sharing with prospects and partners.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-link-status-0',
      popover: {
        title: 'Search Outreach Links',
        description:
          'Search for specific outreach links by name or other criteria to find them quickly.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-link-delete-0',
      popover: {
        title: 'Delete Link',
        description: 'Delete an outreach link to remove it from the system.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-outreach-item-0',
  },
  {
    driverConfig: {
      element: '#tour-link-details',
      popover: {
        title: 'Link Details',
        description: 'View and edit details for this outreach link.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-link-visibility',
      popover: {
        title: 'Link Visibility',
        description:
          'View and edit visibility settings for this outreach link.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-view-submissions',
      popover: {
        title: 'View submissions',
        description: 'Click here to view submissions.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-link-view-submissions',
      popover: {
        title: 'View Link Submissions',
        description: 'View submissions for this outreach link.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-standard-links',
      popover: {
        title: 'Copy and view link',
        description: 'Copy and view outreach link to share records.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-qr-code-container',
      popover: {
        title: 'QR Code',
        description:
          'Scan or download the QR code version of your outreach link.',
        side: TourSide.Bottom,
        align: TourAlignment.Start,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'secondary-popup-close',
    requireDynamicDOM: true,
  },
];

const leadCriteriaSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-search-lead-criteria',
      popover: {
        title: 'Search Lead Criteria',
        description:
          'Search for specific lead criteria rules by name or other criteria to find them quickly.',
        side: TourSide.Right,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-add-lead-criteria',
      popover: {
        title: 'Add Lead Criteria',
        description:
          'Create a new lead criteria rule to define how leads are matched and included.',
        side: TourSide.Right,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-criteria-item-0',
      popover: {
        title: 'Lead Criteria',
        description:
          'Each criteria rule controls how leads are matched and included.',
        side: TourSide.Right,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-criteria-toggle-0',
      popover: {
        title: 'Activate / Deactivate',
        description: 'Enable or disable this criteria without deleting it.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-criteria-delete-item-0',
      popover: {
        title: 'Delete Criteria',
        description: 'Delete this criteria rule permanently.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
  },
];

const notificationSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-notification-item-0',
      popover: {
        title: 'Notification',
        description:
          'View important updates and activity alerts here. Select a notification to review details or manage its status.',
        side: TourSide.Left,
        align: TourAlignment.Start,
      },
    },
    action: TourStepAction.Checkbox,
    actionTargetId: 'tour-notification-checkmark',
  },
  {
    driverConfig: {
      element: '#tour-notification-mark-read',
      popover: {
        title: 'Mark as Read',
        description:
          'Mark selected notifications as read to keep your notification list organized.',
        side: TourSide.Bottom,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-notification-mark-unread',
      popover: {
        title: 'Mark as Unread',
        description:
          'Mark notifications as unread if you want to revisit them later.',
        side: TourSide.Bottom,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-notification-clear',
      popover: {
        title: 'Clear Notifications',
        description:
          'Remove selected notifications from your list once they are no longer needed.',
        side: TourSide.Bottom,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
    action: TourStepAction.Click,
    actionTargetId: 'tour-notification-settings',
  },
  {
    driverConfig: {
      element: '#tour-app-notifications',
      popover: {
        title: 'App Notifications',
        description: 'Control which alerts you receive inside the application.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
    action: TourStepAction.Checkbox,
    actionTargetId: 'tour-notification-checkmark',
  },
  {
    driverConfig: {
      element: '#tour-email-notifications',
      popover: {
        title: 'Email Notifications',
        description:
          'Enable or disable email alerts for important updates and activities.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
    action: TourStepAction.Click,
    actionTargetId: 'tour-close-popup',
  },
];

const profileSteps = [
  {
    driverConfig: {
      element: '#tour-my-profile-tab',
      popover: {
        title: 'My Profile',
        description: 'View and manage your personal profile information here.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-profile-upload',
      popover: {
        title: 'Profile Picture',
        description:
          'Upload or update your profile picture to personalize your account and help others identify you.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-profile-details',
      popover: {
        title: 'Profile Details',
        description: 'View first name, last name and email here.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-email-preference',
      popover: {
        title: 'Email Preferences',
        description:
          'View your current email status and update your preferences to receive emails.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-revoke-sessions',
      popover: {
        title: 'Revoke All Sessions',
        description:
          'Sign out from all active sessions across devices to improve account security.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-device-group-0',
  },
  {
    driverConfig: {
      element: '#tour-session-item-0',
      popover: {
        title: 'Active Sessions',
        description:
          'Review all active login sessions, including device, browser, and last activity details.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
    requireDynamicDOM: true,
    action: TourStepAction.Click,
    actionTargetId: 'tour-device-group-0',
  },
  {
    driverConfig: {
      element: '#tour-cdfi-profile-tab',
      popover: {
        title: 'Organization Profile',
        description: 'View your organization public profile details here.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-cdfi-profile-tab',
    permissions: [
      PermissionCode.MANAGE_CDFI_PROFILE,
      PermissionCode.ADMIN_PRIVILEGES,
    ],
    descriptionWithPermission:
      'Manage your organization public profile details here.',
  },
  {
    driverConfig: {
      element: '#tour-cdfi-profile-card',
      popover: {
        title: 'Organization Details',
        description: 'View your organization’s contact info and description.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
    requireDynamicDOM: true,
    permissions: [
      PermissionCode.MANAGE_CDFI_PROFILE,
      PermissionCode.ADMIN_PRIVILEGES,
    ],
    descriptionWithPermission:
      'View and update your organization’s contact info and description.',
  },
  {
    driverConfig: {
      element: '#tour-cdfi-logo',
      popover: {
        title: 'Organization Logo',
        description: 'Upload or update your organization logo.',
        side: TourSide.Right,
        align: TourAlignment.Center,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-cdfi-edit-btn',
      popover: {
        title: 'Edit Profile',
        description: 'Click here to enable editing for the profile fields.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
    requireDynamicDOM: true,
  },
];

const searchPageSteps = [
  {
    driverConfig: {
      element: '#tour-search-page-controls',
      popover: {
        title: 'Lead source selector',
        description:
          'Select the source you want to discover leads from, including WNCSBI, Data Axle, and more.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-filter-leads-search',
      popover: {
        title: 'Filter Leads',
        description: 'Filter leads by name within the selected source.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-record-assigned-item',
      popover: {
        title: 'Business Record',
        description: 'Each record represents a business added to the system.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
    action: TourStepAction.Checkbox,
    actionTargetId: 'tour-record-item-checkbox',
  },
  {
    driverConfig: {
      element: '#tour-custom-view-map',
      popover: {
        title: 'View on Map',
        description: 'Open this business location directly on the map.',
        side: TourSide.Bottom,
        align: TourAlignment.End,
      },
    },
    action: TourStepAction.Checkbox,
    actionTargetId: 'tour-record-item-checkbox',
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-record-assigned-item-assigned-to',
      popover: {
        title: 'Assigned To',
        description: 'Displays which CDFI this business is assigned to.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-record-item-view',
  },
  ...viewRecordDetails,
];

const organizationsSteps = [
  {
    driverConfig: {
      element: '#tour-org-profile-views',
      popover: {
        title: 'Profile Views',
        description:
          'See how many times your profile has been viewed by organizations.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-assigned-partners',
      popover: {
        title: 'Assigned Partners',
        description:
          'View the total number of organizations currently assigned to your business profile.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-total-funded',
      popover: {
        title: 'Total Funded',
        description:
          'Track the total funding amount received for your business.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-organization-search',
      popover: {
        title: 'Search Organizations',
        description:
          'Use this search box to quickly find organizations by their name.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-organization-item-0',
      popover: {
        title: 'Organization Details',
        description: 'View Organization details',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
];

const profileDetailsSteps = [
  {
    driverConfig: {
      element: '#tour-view-profile-details',
      popover: {
        title: 'Profile Details',
        description: 'View key information about the organization here.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-funding-activity',
      popover: {
        title: 'Funding Activity',
        description:
          'Review the organization’s funding history and lending activity to better understand its experience and impact.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-products-offered',
      popover: {
        title: 'Products Offered',
        description:
          'Explore the financing products and services available from this organization.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-service-area',
      popover: {
        title: 'Service Area',
        description:
          'See the geographic regions and communities served by this organization.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
];

const businessSteps = [
  {
    driverConfig: {
      element: '#tour-assignees',
      popover: {
        title: 'Assignees',
        description:
          'View the organizations that are assigned to your business profile.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-matched-connections',
      popover: {
        title: 'Matched Organizations',
        description:
          'View the organizations that have been matched for your business profile.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-organization-views',
      popover: {
        title: 'Organization Views',
        description:
          'Total number of times that organizations has viewed your business profile',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-basic-info',
      popover: {
        title: 'Basic Information',
        description: 'View the basic information about your business.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-edit-info',
      popover: {
        title: 'Edit Information',
        description: 'Modify the basic information about your business.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-products-services',
      popover: {
        title: 'Products and Services',
        description:
          'Your selected products and services are listed over here. Click each item to view and update details to assist you better.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-edit-products',
      popover: {
        title: 'Edit Products and Services',
        description: 'Modify your products and services',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-business-documents',
      popover: {
        title: 'Business Documents',
        description:
          'Upload supporting documents (such as financial statements, tax records, or certifications) to help organizations evaluate your profile.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-delete-business',
      popover: {
        title: 'Delete Business',
        description: 'Option to delete your business',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
];

const achievementsSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-achievements-summary',
      popover: {
        title: 'Achievements Overview',
        description:
          'Track your total achievements unlocked, milestone completion, and overall progress percentage across all categories.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-achievements-recently-unlocked',
      popover: {
        title: 'Recently Unlocked Badges',
        description:
          'Quickly review your latest accomplishments. Click any badge chip to jump directly to its card with a celebration animation.',
        side: TourSide.Bottom,
        align: TourAlignment.Start,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-achievement-section-0',
      popover: {
        title: 'Milestone Categories',
        description:
          'Achievements are organized into key categories like Referrals, Lead Assignments, Status Updates, and Funding. Complete all milestones in a category to earn the mastery trophy.',
        side: TourSide.Top,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-achievement-action-btn-0',
      popover: {
        title: 'Take Action to Progress',
        description:
          'Use this shortcut button to navigate directly to the workspace or tool where you can perform qualifying actions to earn your next badge.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-achievement-card-0',
      popover: {
        title: 'Achievement Badges',
        description:
          'Each card displays a 3D metallic shield badge, tier rank, action requirements, and your progress. Click to view detailed criteria in the spotlight modal.',
        side: TourSide.Right,
        align: TourAlignment.Start,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-achievement-card-0',
  },
  {
    driverConfig: {
      element: '#tour-achievement-modal',
      popover: {
        title: 'Achievement Spotlight',
        description:
          'Inspect in-depth milestone information, tier badges, actions required, and exact progress towards unlocking the badge.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
    requireDynamicDOM: true,
    action: TourStepAction.Click,
    actionTargetId: 'tour-achievement-modal-close',
  },
];

const flagsSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-flags-filters',
      popover: {
        title: 'Filter Flags',
        description:
          'Filter your reported flags by Entity Type, Status, or Severity to locate specific submissions quickly.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-flag-row-0',
      popover: {
        title: 'Flagged Records',
        description:
          'Review your submitted reports including the entity type, reason for flagging, severity, and status.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-flag-status-0',
      popover: {
        title: 'Resolution Status',
        description:
          'Monitor whether your report is currently Pending, In Progress, or Resolved by an administrator.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-flag-view-btn-0',
      popover: {
        title: 'View Flag Details',
        description:
          'Open the details panel to view attached files, entity information, and administrative resolution notes.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-flag-view-btn-0',
  },
  {
    driverConfig: {
      element: '#tour-flag-details-meta',
      popover: {
        title: 'Flag Overview & Severity',
        description:
          'Inspect the entity type, assigned severity rating, current resolution status, and creation date.',
        side: TourSide.Left,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-flag-details-reason',
      popover: {
        title: 'Reason & Details',
        description:
          'Review the specific reason and detailed explanation submitted with this report.',
        side: TourSide.Left,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-flag-details-files',
      popover: {
        title: 'Attached Files',
        description:
          'Access supporting files and screenshots uploaded with the flag. Click to preview or download.',
        side: TourSide.Left,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-flag-details-resolution',
      popover: {
        title: 'Resolution Notes',
        description:
          'Review notes and feedback added by administrators explaining how the issue was resolved.',
        side: TourSide.Left,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
    action: TourStepAction.Click,
    actionTargetId: 'secondary-popup-close',
  },
];

const manageFlagsSteps: TourStep[] = [
  {
    driverConfig: {
      element: '#tour-flags-filters',
      popover: {
        title: 'Filter Flags',
        description:
          'Filter flagged reports across all users by Entity Type, Status, or Severity level.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-flag-row-0',
      popover: {
        title: 'Flagged Reports Table',
        description:
          'Review submitted flags from users across the system, including reporter details, severity, and submission dates.',
        side: TourSide.Bottom,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-flag-status-0',
      popover: {
        title: 'Flag Status',
        description:
          'Track whether a report is Pending review, In Progress, or Resolved.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
  },
  {
    driverConfig: {
      element: '#tour-flag-view-btn-0',
      popover: {
        title: 'Inspect & Manage Flag',
        description:
          'Open the details side panel to review user-submitted details, examine linked entities, and take action.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
    action: TourStepAction.Click,
    actionTargetId: 'tour-flag-view-btn-0',
  },
  {
    driverConfig: {
      element: '#tour-flag-details-meta',
      popover: {
        title: 'Flag Overview & Severity',
        description:
          'Inspect the entity type, severity rating, current resolution status, and creation date.',
        side: TourSide.Left,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-flag-details-reason',
      popover: {
        title: 'Reason & Details',
        description:
          'Review the specific problem reported along with the detailed description provided by the user.',
        side: TourSide.Left,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-flag-details-files',
      popover: {
        title: 'Attached Files',
        description:
          'Preview or download supporting evidence, documents, or screenshots uploaded by the user.',
        side: TourSide.Left,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-flag-details-entity',
      popover: {
        title: 'Reported Entity Details',
        description:
          'Inspect information about the specific entity (lead, note, file, notification, or business) linked to this flag.',
        side: TourSide.Left,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-secondary-popup-primary-btn',
      popover: {
        title: 'Take Action',
        description:
          'Click Take Action to update the flag status (e.g. In Progress, Resolved) and enter resolution notes.',
        side: TourSide.Left,
        align: TourAlignment.Center,
      },
    },
    requireDynamicDOM: true,
  },
  {
    driverConfig: {
      element: '#tour-flag-details-resolution',
      popover: {
        title: 'Resolution Notes',
        description:
          'Add or review administrative resolution notes detailing the outcome and communication with the reporter.',
        side: TourSide.Left,
        align: TourAlignment.Start,
      },
    },
    requireDynamicDOM: true,
    action: TourStepAction.Click,
    actionTargetId: 'secondary-popup-close',
  },
];

export const TourConfigList: Record<string, TourRouteConfig> = {
  '/dashboard': {
    defaultSteps: dashboardSteps,
    optionalSteps: smeDashboardSteps,
  },
  '/leads/my-leads': { defaultSteps: leadsSteps },
  '/files': { defaultSteps: filesSteps },
  '/deeproots/links': { defaultSteps: linksSteps },
  '/deeproots/leads-criteria': { defaultSteps: leadCriteriaSteps },
  '/notifications': { defaultSteps: notificationSteps },
  '/profile': { defaultSteps: profileSteps },
  '/search': { defaultSteps: searchPageSteps },
  '/leads/find': {
    defaultSteps: findLeadsSteps,
    matchers: [
      {
        match: (p) => p['tab'] === 'deeproots' && p['subtab'] === 'inquiries',
        steps: [...findLeadsSteps, ...inquiriesSteps],
      },
      {
        match: (p) => p['tab'] === 'deeproots' && p['subtab'] === 'matched',
        steps: [...findLeadsSteps, ...matchedRecordsSteps],
      },
      {
        match: (p) => p['tab'] === 'custom',
        steps: [...findLeadsSteps, ...customBusinessSteps],
      },
      {
        match: (p) => p['tab'] === 'wncsbi',
        steps: [...findLeadsSteps, ...wncsbiSteps],
      },
      {
        match: (p) => p['tab'] === 'data-axle',
        steps: [...findLeadsSteps, ...initialDataAxleSteps],
      },
    ],
  },
  '/cdfis': { defaultSteps: organizationsSteps },
  '/cdfi/profile': { defaultSteps: profileDetailsSteps },
  '/business': { defaultSteps: businessSteps },
  '/achievements': { defaultSteps: achievementsSteps },
  '/flags': { defaultSteps: flagsSteps },
  '/manage-flags': { defaultSteps: manageFlagsSteps },
};

export const TourStaticSteps: Record<string, TourStep[]> = {
  header: headerSteps,
  sidebarMenu: sideMenuSteps,
};
